import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Clock, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import { EditorialHeader, MarqueeBand, Reveal, Eyebrow } from "@/components/editorial";

const categories = [
  "All",
  "School Organization",
  "Parent Systems",
  "Executive Function",
  "Family Planning",
  "Elementary School Tips",
];

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("All");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (cat !== "All") params.category = cat;
        if (search) params.q = search;
        const { data } = await api.get("/posts", { params });
        setPosts(data);
      } finally {
        setLoading(false);
      }
    };
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [cat, search]);

  const [featured, ...rest] = posts;

  return (
    <div data-testid="blog-page">
      <Seo
        title="The Reader"
        description="Practical strategies on school organization, parent systems, executive function, and family planning, for elementary school parents."
        path="/blog"
      />

      <EditorialHeader
        chapter="Vol. 04"
        eyebrow="The Reader"
        title="Stay one step ahead,"
        italicWord="one read at a time."
        intro="Practical strategies on school organization, parent systems, executive function, and family planning. New editions on the regular."
        testid="blog-header"
      />

      <MarqueeBand items={["School Organization", "Parent Systems", "Executive Function", "Family Planning", "Elementary Tips"]} />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-28">
        {/* Search + Filters */}
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-10">
            <div className="relative w-full md:max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-inkMuted" />
              <input
                data-testid="blog-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                type="text"
                placeholder="Search articles..."
                className="w-full bg-white border border-brand-border rounded-full pl-11 pr-5 py-3.5 outline-none focus:border-brand-sage transition-colors"
              />
            </div>
            <Eyebrow>Chapter 01, the archive</Eyebrow>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="flex flex-wrap gap-2 mb-14" data-testid="blog-categories">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`category-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.18em] font-semibold border transition-colors ${
                  cat === c
                    ? "bg-brand-ink text-brand-bg border-brand-ink"
                    : "bg-white text-brand-ink border-brand-border hover:border-brand-ink"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-2xl bg-white border border-brand-border p-4">
                <div className="aspect-[4/3] rounded-xl bg-brand-bg animate-pulse" />
                <div className="mt-4 h-3 w-20 bg-brand-bg rounded animate-pulse" />
                <div className="mt-2 h-5 w-full bg-brand-bg rounded animate-pulse" />
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <p data-testid="blog-empty" className="text-brand-inkMuted">
            No articles match your search. Try a different keyword or category.
          </p>
        ) : (
          <div>
            {/* FEATURED POST — huge editorial spread */}
            {featured && (
              <Reveal>
                <Link
                  to={`/blog/${featured.slug}`}
                  data-testid="blog-featured"
                  className="group block mb-20 md:mb-28"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center">
                    <div className="lg:col-span-7 order-2 lg:order-1">
                      <p className="text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-accent">
                        Featured, this week&apos;s edition
                      </p>
                      <h2 className="mt-4 font-display font-medium tracking-[-0.02em] text-brand-ink leading-[1.02] text-4xl sm:text-5xl md:text-6xl lg:text-[68px]">
                        {featured.title.split(" ").map((w, i) =>
                          i % 4 === 2 ? (
                            <span key={i} className="font-editorial italic font-normal text-brand-sage">
                              {w}{" "}
                            </span>
                          ) : (
                            <span key={i}>{w} </span>
                          )
                        )}
                      </h2>
                      <p className="mt-6 text-brand-inkSoft text-base sm:text-lg leading-relaxed max-w-xl">
                        {featured.excerpt}
                      </p>
                      <div className="mt-8 flex items-center gap-4 text-[11px] uppercase tracking-[0.22em] font-semibold text-brand-inkMuted">
                        <span className="text-brand-sage">{featured.category}</span>
                        <span className="inline-block h-px w-8 bg-brand-inkMuted/40" />
                        <span className="inline-flex items-center gap-1.5">
                          <Clock className="h-3 w-3" /> {featured.read_minutes} min
                        </span>
                      </div>
                      <span className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-brand-ink group-hover:text-brand-sageDark transition-colors">
                        <span className="border-b border-brand-ink/40 group-hover:border-brand-sageDark/60 pb-0.5">
                          Read the edition
                        </span>
                        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </span>
                    </div>
                    <div className="lg:col-span-5 order-1 lg:order-2 relative">
                      <div className="absolute -top-6 -left-2 z-20 select-none pointer-events-none">
                        <span className="font-editorial italic text-brand-accent text-6xl md:text-7xl leading-none">
                          01
                        </span>
                      </div>
                      <div className="spotlight-frame relative rounded-[28px] overflow-hidden aspect-[4/5]">
                        <img
                          src={featured.cover_image}
                          alt={featured.title}
                          loading="eager"
                          className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.04]"
                        />
                        <div className="absolute inset-0 bg-gradient-to-tr from-brand-ink/10 to-transparent" />
                      </div>
                    </div>
                  </div>
                </Link>
              </Reveal>
            )}

            {/* Rest of posts — asymmetric tile grid */}
            <Reveal>
              <div className="mb-10 flex items-end justify-between gap-6 border-t border-brand-border pt-10">
                <Eyebrow>Chapter 02, back issues</Eyebrow>
                <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-brand-inkMuted">
                  {rest.length} {rest.length === 1 ? "edition" : "editions"}
                </span>
              </div>
            </Reveal>

            <div className="grid grid-cols-1 md:grid-cols-6 gap-6 md:gap-8">
              {rest.map((p, i) => {
                // Alternate span for editorial rhythm
                const isWide = i % 5 === 0 || i % 5 === 3;
                return (
                  <motion.article
                    key={p.id}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                    data-testid={`blog-card-${i}`}
                    className={isWide ? "md:col-span-4" : "md:col-span-2"}
                  >
                    <Link
                      to={`/blog/${p.slug}`}
                      className="group block h-full rounded-[20px] bg-white border border-brand-border overflow-hidden hover:border-brand-ink transition-colors"
                    >
                      <div className={`overflow-hidden ${isWide ? "aspect-[16/9]" : "aspect-[4/3]"}`}>
                        <img
                          src={p.cover_image}
                          alt={p.title}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.05]"
                        />
                      </div>
                      <div className={`${isWide ? "p-7 md:p-8" : "p-5 md:p-6"}`}>
                        <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] font-semibold text-brand-inkMuted">
                          <span className="text-brand-sage">{p.category}</span>
                          <span className="inline-block h-px w-6 bg-brand-inkMuted/40" />
                          <span className="inline-flex items-center gap-1.5">
                            <Clock className="h-3 w-3" /> {p.read_minutes} min
                          </span>
                        </div>
                        <h3
                          className={`mt-4 font-display font-medium text-brand-ink leading-[1.15] group-hover:text-brand-sageDark transition-colors ${
                            isWide ? "text-2xl md:text-3xl" : "text-xl"
                          }`}
                        >
                          {p.title}
                        </h3>
                        {isWide && (
                          <p className="mt-3 text-brand-inkSoft leading-relaxed line-clamp-2">
                            {p.excerpt}
                          </p>
                        )}
                        <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-medium text-brand-sageDark">
                          Read <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </span>
                      </div>
                    </Link>
                  </motion.article>
                );
              })}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
